import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Dropdown, Modal } from 'react-bootstrap';
import { FaClipboardList, FaCalendar, FaDownload, FaFilePdf, FaFileExcel, FaSearch, FaEye, FaEllipsisV, FaTimes, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { coverageAPI, branchesAPI, regionsAPI } from '../../utils/api';
import { getCurrentUser, hasPermission } from '../../utils/permissionUtils';
import notificationService from '../../services/notificationService';
import '../../styles/modern-design-system.css';
import './Coverage.css';

const CoverageView = () => {
  const [coverages, setCoverages] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  const [editFormData, setEditFormData] = useState({
    siteName: '',
    branch: '',
    region: '',
    dateFrom: '',
    dateTo: '',
    isSingleDay: false,
    numColumns: 1,
    guards: []
  });
  const [saving, setSaving] = useState(false);
  
  // Get current user and check permissions
  const currentUser = getCurrentUser();
  const canExport = currentUser?.role === 'admin' || hasPermission(currentUser, 'coverage', 'export');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  
  // Branches and Regions from database
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);

  // Fetch coverages, branches, and regions on component mount
  useEffect(() => {
    fetchCoverages();
    fetchBranches();
    fetchRegions();
  }, []);

  const fetchCoverages = async () => {
    try {
      setLoading(true);
      const response = await coverageAPI.getAll();
      setCoverages(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching coverages:', err);
      setCoverages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await branchesAPI.getAll();
      setBranches(response.data || []);
    } catch (err) {
      console.error('Error fetching branches:', err);
      setBranches([]);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await regionsAPI.getAll();
      setRegions(response.data || []);
    } catch (err) {
      console.error('Error fetching regions:', err);
      setRegions([]);
    }
  };



  const handleOpenEditModal = (coverage) => {
    setSelectedCoverage(coverage);
    // Prepare edit form data - ensure guards have payment fields
    const guards = (coverage.guards || []).map(g => ({
      name: g.name || '',
      phone: g.phone || '',
      payType: g.payType || 'PAYABLE', // default to PAYABLE for backwards compatibility
      amount: g.amount || (g.payType !== 'NON_PAYABLE' ? 500 : 0)
    }));
    
    setEditFormData({
      siteName: coverage.siteName || '',
      branch: coverage.branch || '',
      region: coverage.region || '',
      dateFrom: coverage.dateFrom ? new Date(coverage.dateFrom).toISOString().split('T')[0] : '',
      dateTo: coverage.dateTo ? new Date(coverage.dateTo).toISOString().split('T')[0] : '',
      isSingleDay: coverage.isSingleDay || false,
      numColumns: coverage.numColumns || coverage.guards?.length || 1,
      guards: guards
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditFormData({
      siteName: '',
      branch: '',
      region: '',
      dateFrom: '',
      dateTo: '',
      isSingleDay: false,
      numColumns: 1,
      guards: []
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGuardChange = (index, field, value) => {
    setEditFormData(prev => {
      const updatedGuards = [...prev.guards];
      if (!updatedGuards[index]) {
        updatedGuards[index] = { name: '', phone: '', payType: 'PAYABLE', amount: 500 };
      }
      updatedGuards[index] = {
        ...updatedGuards[index],
        [field]: value
      };
      return {
        ...prev,
        guards: updatedGuards
      };
    });
  };

  const handleAddGuardColumn = () => {
    setEditFormData(prev => ({
      ...prev,
      numColumns: prev.numColumns + 1,
      guards: [...prev.guards, { name: '', phone: '', payType: 'PAYABLE', amount: 500 }]
    }));
  };

  const handleRemoveGuardColumn = (index) => {
    if (editFormData.numColumns <= 1) {
      setError('At least one guard column is required');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setEditFormData(prev => ({
      ...prev,
      numColumns: prev.numColumns - 1,
      guards: prev.guards.filter((_, i) => i !== index)
    }));
  };

  const handleSaveEdit = async () => {
    // Validation
    if (!editFormData.siteName.trim()) {
      setError('Site name is required');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!editFormData.dateFrom) {
      setError('Start date is required');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!editFormData.isSingleDay && !editFormData.dateTo) {
      setError('End date is required for multi-day coverage');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setSaving(true);
      
      const updateData = {
        siteName: editFormData.siteName.trim(),
        branch: editFormData.branch.trim(),
        region: editFormData.region.trim(),
        dateFrom: editFormData.dateFrom,
        dateTo: editFormData.isSingleDay ? editFormData.dateFrom : editFormData.dateTo,
        isSingleDay: editFormData.isSingleDay,
        numColumns: editFormData.numColumns,
        guards: editFormData.guards
      };

      await coverageAPI.update(selectedCoverage.id, updateData);
      
      // Trigger notification to admin and sub-admins
      const currentAdmin = getCurrentUser();
      const creatorName = selectedCoverage.creator?.name || 
                          selectedCoverage.createdBy?.name || 
                          selectedCoverage.personnelName || 
                          'Personnel';
      
      notificationService.triggerCoverageAction('coverage-edited', {
        adminName: currentAdmin?.name || currentAdmin?.username || 'Admin',
        adminId: currentAdmin?.id || currentAdmin?.userId,
        personnelName: creatorName,
        siteName: updateData.siteName,
        branch: updateData.branch,
        region: updateData.region
      });
      
      // Update local state
      setCoverages(prevCoverages => 
        prevCoverages.map(cov => 
          cov.id === selectedCoverage.id ? { ...cov, ...updateData } : cov
        )
      );
      
      setSuccess('Coverage updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      handleCloseEditModal();
      setSelectedCoverage(null);
    } catch (err) {
      console.error('Error updating coverage:', err);
      setError(err.response?.data?.message || 'Failed to update coverage. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      
      // Prepare filters as query parameters
      const exportFilters = {
        dateFrom: filterDateFrom || '',
        dateTo: filterDateTo || '',
        branch: filterBranch || '',
        region: filterRegion || ''
      };
      
      // Only add search parameters if searchTerm exists
      if (searchTerm) {
        // Check if searchTerm looks like a phone number (contains digits, +, -, spaces, parentheses)
        const looksLikePhone = /^[\d\s+\-()]+$/.test(searchTerm.trim());
        
        if (looksLikePhone) {
          // Search by phone number only
          exportFilters.guardPhone = searchTerm;
        } else {
          // Check if search term matches a site name in current data
          const matchesSite = coverages.some(cov => 
            cov.siteName?.toLowerCase().includes(searchTerm.toLowerCase())
          );
          
          // Check if search term matches a guard name in current data
          const matchesGuard = coverages.some(cov => 
            cov.guards?.some(guard => 
              guard.name?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          );
          
          // Send only the parameter that makes sense
          if (matchesSite && !matchesGuard) {
            // Only a site name
            exportFilters.siteName = searchTerm;
          } else if (matchesGuard && !matchesSite) {
            // Only a guard name
            exportFilters.guardName = searchTerm;
          } else {
            // Could be either or both - send both
            exportFilters.siteName = searchTerm;
            exportFilters.guardName = searchTerm;
          }
        }
      }
      
      const response = await coverageAPI.exportPDF(exportFilters);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coverage_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('PDF exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      
      // Prepare filters as query parameters
      const exportFilters = {
        dateFrom: filterDateFrom || '',
        dateTo: filterDateTo || '',
        branch: filterBranch || '',
        region: filterRegion || ''
      };
      
      // Only add search parameters if searchTerm exists
      if (searchTerm) {
        // Check if searchTerm looks like a phone number (contains digits, +, -, spaces, parentheses)
        const looksLikePhone = /^[\d\s+\-()]+$/.test(searchTerm.trim());
        
        if (looksLikePhone) {
          // Search by phone number only
          exportFilters.guardPhone = searchTerm;
        } else {
          // Check if search term matches a site name in current data
          const matchesSite = coverages.some(cov => 
            cov.siteName?.toLowerCase().includes(searchTerm.toLowerCase())
          );
          
          // Check if search term matches a guard name in current data
          const matchesGuard = coverages.some(cov => 
            cov.guards?.some(guard => 
              guard.name?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          );
          
          // Send only the parameter that makes sense
          if (matchesSite && !matchesGuard) {
            // Only a site name
            exportFilters.siteName = searchTerm;
          } else if (matchesGuard && !matchesSite) {
            // Only a guard name
            exportFilters.guardName = searchTerm;
          } else {
            // Could be either or both - send both
            exportFilters.siteName = searchTerm;
            exportFilters.guardName = searchTerm;
          }
        }
      }
      
      const response = await coverageAPI.exportExcel(exportFilters);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coverage_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Excel exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError('Failed to export Excel. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = (from, to) => {
    const fromDate = new Date(from).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const toDate = new Date(to).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fromDate} - ${toDate}`;
  };

  const calculateDuration = (from, to) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffTime = Math.abs(toDate - fromDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  };

  // Get unique branches from coverages
  const getUniqueBranches = () => {
    const branches = coverages
      .map(cov => cov.branch)
      .filter(branch => branch && branch.trim())
      .filter((branch, index, self) => self.indexOf(branch) === index)
      .sort();
    return branches;
  };

  // Get unique regions from coverages
  const getUniqueRegions = () => {
    const regions = coverages
      .map(cov => cov.region)
      .filter(region => region && region.trim())
      .filter((region, index, self) => self.indexOf(region) === index)
      .sort();
    return regions;
  };

  // Get unique guards with multiple site assignments
  const getGuardsWithMultipleSites = () => {
    const guardSites = new Map();
    
    coverages.forEach(coverage => {
      coverage.guards?.forEach(guard => {
        if (guard.name && guard.name.trim()) {
          const guardKey = guard.name.toLowerCase().trim();
          
          if (!guardSites.has(guardKey)) {
            guardSites.set(guardKey, {
              name: guard.name.trim(),
              phone: guard.phone,
              sites: []
            });
          }
          
          guardSites.get(guardKey).sites.push({
            siteName: coverage.siteName,
            branch: coverage.branch,
            region: coverage.region,
            dateFrom: coverage.dateFrom,
            dateTo: coverage.dateTo,
            isSingleDay: coverage.isSingleDay,
            amount: parseFloat(guard.amount) || 0,
            payType: guard.payType
          });
        }
      });
    });
    
    // Filter only guards with multiple sites
    const multiSiteGuards = Array.from(guardSites.values()).filter(guard => guard.sites.length > 1);
    return multiSiteGuards;
  };

  // Filter coverages based on search term and date filters
  const filteredCoverages = coverages.filter(coverage => {
    // Search term filter (search in site name and guard names)
    const matchesSearch = searchTerm === '' || 
      coverage.siteName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coverage.guards?.some(guard => 
        guard.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guard.phone?.includes(searchTerm)
      );
    
    // Date range filter
    let matchesDateRange = true;
    if (filterDateFrom || filterDateTo) {
      const coverageStart = new Date(coverage.dateFrom);
      const coverageEnd = new Date(coverage.dateTo);
      
      if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        matchesDateRange = matchesDateRange && (coverageEnd >= fromDate);
      }
      
      if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        matchesDateRange = matchesDateRange && (coverageStart <= toDate);
      }
    }
    
    // Branch filter
    const matchesBranch = filterBranch === '' || 
      coverage.branch === filterBranch;
    
    // Region filter
    const matchesRegion = filterRegion === '' || 
      coverage.region === filterRegion;
    
    return matchesSearch && matchesDateRange && matchesBranch && matchesRegion;
  });

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-2" style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 700,
                background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-blue-light))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                <FaEye className="me-2" style={{ color: 'var(--accent-blue)' }} />
                Coverage Overview
              </h1>
              <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                View personnel coverage assignments and export reports • {coverages.length} active {coverages.length === 1 ? 'assignment' : 'assignments'}
                {getGuardsWithMultipleSites().length > 0 && (
                  <> • {getGuardsWithMultipleSites().length} {getGuardsWithMultipleSites().length === 1 ? 'guard' : 'guards'} at multiple sites</>
                )}
              </p>
            </div>
            <div className="d-flex gap-2">
              <Dropdown>
                <Dropdown.Toggle 
                  variant="primary"
                  className="d-flex align-items-center gap-2"
                  style={{
                    padding: '0.625rem 1.5rem',
                    fontWeight: 600
                  }}
                  disabled={!canExport || coverages.length === 0}
                  title={!canExport ? "You don't have permission to export" : ""}
                >
                  <FaDownload />
                  Export Report
                </Dropdown.Toggle>

                <Dropdown.Menu 
                  align="end"
                  style={{
                    background: 'var(--surface-glass)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '0.5rem',
                    minWidth: '200px'
                  }}
                >
                  <Dropdown.Item
                    onClick={handleExportPDF}
                    style={{
                      color: 'var(--text-primary)',
                      padding: '0.75rem 1rem',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <FaFilePdf style={{ color: '#dc3545', fontSize: '1.25rem' }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>Export as PDF</div>
                      <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        Professional report format
                      </small>
                    </div>
                  </Dropdown.Item>
                  
                  <Dropdown.Divider style={{ margin: '0.5rem 0', borderColor: 'var(--border-color)' }} />
                  
                  <Dropdown.Item
                    onClick={handleExportExcel}
                    style={{
                      color: 'var(--text-primary)',
                      padding: '0.75rem 1rem',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <FaFileExcel style={{ color: '#28a745', fontSize: '1.25rem' }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>Export as Excel</div>
                      <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        Spreadsheet with all data
                      </small>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card className="shadow-sm mb-4" style={{
        background: 'var(--surface-glass)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-xl)'
      }}>
        <Card.Body>
          <Row className="g-3">
            <Col md={12}>
              <div style={{ position: 'relative' }}>
                <FaSearch style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <Form.Control
                  type="text"
                  placeholder="Search by site name, guard name, or phone number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '2.5rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            </Col>
          </Row>
          
          <Row className="g-3 mt-2">
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Date From
              </Form.Label>
              <Form.Control
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Date To
              </Form.Label>
              <Form.Control
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)'
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Branch
              </Form.Label>
              <Form.Select
                value={filterBranch}
                onChange={(e) => setFilterBranch(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)',
                  padding: '0.6rem 0.75rem'
                }}
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch.id || branch.name} value={branch.name}>{branch.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Region
              </Form.Label>
              <Form.Select
                value={filterRegion}
                onChange={(e) => setFilterRegion(e.target.value)}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-primary)',
                  padding: '0.6rem 0.75rem'
                }}
              >
                <option value="">All Regions</option>
                {regions.map(region => (
                  <option key={region.id || region.name} value={region.name}>{region.name}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          
          {(searchTerm || filterDateFrom || filterDateTo || filterBranch || filterRegion) && (
            <div className="mt-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                  setFilterBranch('');
                  setFilterRegion('');
                }}
              >
                Clear All Filters
              </Button>
              <span className="ms-3" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Showing {filteredCoverages.length} of {coverages.length} {coverages.length === 1 ? 'assignment' : 'assignments'}
              </span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Coverage List */}
      {loading ? (
        <Card className="shadow-sm text-center py-5" style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <Card.Body>
            <Spinner animation="border" style={{ color: 'var(--accent-blue)' }} />
            <p className="mt-3" style={{ color: 'var(--text-muted)' }}>Loading coverage assignments...</p>
          </Card.Body>
        </Card>
      ) : filteredCoverages.length > 0 ? (
        <Row className="g-3">
          {filteredCoverages.map((coverage) => (
            <Col key={coverage.id} xs={12}>
              <Card className="coverage-card shadow-sm" style={{
                background: 'var(--surface-glass)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-xl)'
              }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <h5 className="mb-0" style={{ 
                          fontWeight: 700, 
                          color: 'var(--text-primary)',
                          fontSize: '1.25rem'
                        }}>
                          {coverage.siteName}
                        </h5>
                      </div>
                      
                      {/* Branch and Region Information */}
                      {(coverage.branch || coverage.region) && (
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {coverage.branch && (
                            <span className="badge" style={{
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border-color)',
                              padding: '0.375rem 0.75rem',
                              borderRadius: 'var(--radius-md)',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              Branch: {coverage.branch}
                            </span>
                          )}
                          {coverage.region && (
                            <span className="badge" style={{
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border-color)',
                              padding: '0.375rem 0.75rem',
                              borderRadius: 'var(--radius-md)',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              Region: {coverage.region}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="d-flex flex-wrap gap-3 align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <FaCalendar style={{ color: 'var(--accent-blue)', fontSize: '0.9rem' }} />
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            {coverage.isSingleDay 
                              ? new Date(coverage.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : formatDateRange(coverage.dateFrom, coverage.dateTo)
                            }
                          </span>
                        </div>
                        {coverage.isSingleDay ? (
                          <span className="badge" style={{
                            background: 'var(--success)',
                            color: 'white',
                            padding: '0.375rem 0.75rem',
                            borderRadius: 'var(--radius-md)',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            Single Day
                          </span>
                        ) : (
                          <span className="badge" style={{
                            background: 'var(--accent-blue-dark)',
                            color: 'white',
                            padding: '0.375rem 0.75rem',
                            borderRadius: 'var(--radius-md)',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {calculateDuration(coverage.dateFrom, coverage.dateTo)}
                          </span>
                        )}
                      </div>
                      
                      {/* Added by information */}
                      {coverage.creator && (
                        <div className="mt-2 pt-2" style={{ 
                          borderTop: '1px solid var(--border-color)'
                        }}>
                          <div style={{ 
                            fontSize: '0.85rem', 
                            color: 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span style={{ fontWeight: 600 }}>Added by:</span>
                            <span style={{ color: 'var(--text-primary)' }}>
                              {coverage.creator.name}
                            </span>
                            <span style={{ 
                              color: 'var(--text-muted)',
                              fontSize: '0.8rem'
                            }}>
                              ({coverage.creator.email})
                            </span>
                            <span className="badge" style={{
                              background: coverage.creator.role === 'admin' ? 'var(--accent-blue)' : 
                                         coverage.creator.role === 'sub-admin' ? 'var(--warning)' : 
                                         'var(--success)',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              textTransform: 'capitalize'
                            }}>
                              {coverage.creator.role}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        className="d-flex align-items-center gap-2"
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-secondary)',
                          color: 'var(--text-primary)',
                          fontWeight: 600
                        }}
                      >
                        <FaEllipsisV />
                        Actions
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        style={{
                          background: 'var(--surface-glass)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid var(--border-color)',
                          borderRadius: 'var(--radius-lg)',
                          padding: '0.5rem',
                          minWidth: '200px'
                        }}
                      >
                        <Dropdown.Item
                          onClick={() => handleOpenEditModal(coverage)}
                          style={{
                            color: 'var(--text-primary)',
                            padding: '0.75rem 1rem',
                            borderRadius: 'var(--radius-md)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                          }}
                        >
                          <FaEdit style={{ color: 'var(--accent-blue)', fontSize: '1rem' }} />
                          <div>
                            <div style={{ fontWeight: 600 }}>Edit Coverage</div>
                            <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                              Modify details
                            </small>
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  
                  <div className="table-responsive coverage-table-wrapper">
                    <table className="coverage-excel-table">
                      <thead>
                        <tr>
                          {Array.from({ length: coverage.numColumns }, (_, i) => (
                            <th key={i}>Guard {i + 1}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="name-row">
                          {Array.from({ length: coverage.numColumns }, (_, i) => (
                            <td key={i}>
                              <div className="guard-cell">
                                <span className="cell-label">Name:</span>
                                <span className="cell-value">{coverage.guards[i]?.name || '—'}</span>
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr className="phone-row">
                          {Array.from({ length: coverage.numColumns }, (_, i) => (
                            <td key={i}>
                              <div className="guard-cell">
                                <span className="cell-label">Phone:</span>
                                <span className="cell-value">{coverage.guards[i]?.phone || '—'}</span>
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr className="payment-row">
                          {Array.from({ length: coverage.numColumns }, (_, i) => {
                            const guard = coverage.guards[i];
                            const isPayable = guard?.payType === 'PAYABLE'; // use payType
                            const amount = guard?.amount || 0;
                            
                            return (
                              <td key={i}>
                                <div className="guard-cell">
                                  <span className="cell-label">Payment:</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <span 
                                      className="cell-value"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        padding: '0.25rem 0.625rem',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        background: isPayable ? 'rgba(34, 197, 94, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                                        color: isPayable ? 'rgb(34, 197, 94)' : 'rgb(148, 163, 184)',
                                        border: `1px solid ${isPayable ? 'rgba(34, 197, 94, 0.3)' : 'rgba(148, 163, 184, 0.3)'}`
                                      }}
                                    >
                                      <span style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        background: isPayable ? 'rgb(34, 197, 94)' : 'rgb(148, 163, 184)'
                                      }} />
                                      {isPayable ? 'Payable' : 'Non-payable'}
                                    </span>
                                    {isPayable && amount > 0 && (
                                      <span style={{
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: 'var(--accent-blue)'
                                      }}>
                                        KSh {amount.toLocaleString()}
                                      </span>
                                    )}
                                    {!isPayable && (
                                      <span style={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: 'var(--text-muted)'
                                      }}>
                                        KSh 0
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="shadow-sm text-center py-5" style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <Card.Body>
            <FaClipboardList size={64} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
            <p className="mt-3" style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>
              {coverages.length === 0 
                ? "No coverage assignments yet. Personnel can create coverage assignments from their dashboard."
                : "No coverage assignments found matching your search criteria."}
            </p>
            {coverages.length > 0 && (searchTerm || filterDateFrom || filterDateTo || filterBranch || filterRegion) && (
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setSearchTerm('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                  setFilterBranch('');
                  setFilterRegion('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Guards with Multiple Site Assignments */}
      {getGuardsWithMultipleSites().length > 0 && (
        <Card className="shadow-sm mt-4" style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <Card.Body>
            <h5 className="mb-3" style={{ 
              fontWeight: 700, 
              color: 'var(--text-primary)',
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FaClipboardList style={{ color: 'var(--accent-blue)' }} />
              Guards Assigned to Multiple Sites
            </h5>
            <Row className="g-3">
              {getGuardsWithMultipleSites().map((guard, index) => (
                <Col key={index} md={6} lg={4}>
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1rem'
                  }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div>
                        <h6 style={{ 
                          fontWeight: 700, 
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                          fontSize: '1rem'
                        }}>
                          {guard.name}
                        </h6>
                        {guard.phone && (
                          <small style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                            {guard.phone}
                          </small>
                        )}
                      </div>
                      <span className="badge" style={{
                        background: 'var(--accent-blue)',
                        color: 'white',
                        padding: '0.375rem 0.75rem',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        {guard.sites.length} Sites
                      </span>
                    </div>
                    <hr style={{ borderColor: 'var(--border-color)', margin: '0.75rem 0' }} />
                    <div style={{ fontSize: '0.85rem' }}>
                      {guard.sites.map((site, siteIndex) => (
                        <div 
                          key={siteIndex}
                          className="d-flex align-items-start gap-2 mb-2"
                          style={{
                            padding: '0.5rem',
                            background: 'var(--surface-glass)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border-color)'
                          }}
                        >
                          <div style={{ 
                            minWidth: '1.5rem',
                            height: '1.5rem',
                            borderRadius: '50%',
                            background: 'var(--accent-blue)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            flexShrink: 0
                          }}>
                            {siteIndex + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: 600, 
                              color: 'var(--text-primary)',
                              marginBottom: '0.25rem'
                            }}>
                              {site.siteName}
                            </div>
                            {(site.branch || site.region) && (
                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '0.25rem',
                                marginBottom: '0.25rem'
                              }}>
                                {site.branch && (
                                  <span style={{
                                    fontSize: '0.65rem',
                                    color: 'var(--accent-blue)',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '0.25rem',
                                    fontWeight: 600
                                  }}>
                                    {site.branch}
                                  </span>
                                )}
                                {site.region && (
                                  <span style={{
                                    fontSize: '0.65rem',
                                    color: 'var(--success)',
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '0.25rem',
                                    fontWeight: 600
                                  }}>
                                    {site.region}
                                  </span>
                                )}
                              </div>
                            )}
                            <div style={{ 
                              color: 'var(--text-muted)', 
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <FaCalendar style={{ fontSize: '0.7rem' }} />
                              {site.isSingleDay 
                                ? new Date(site.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : formatDateRange(site.dateFrom, site.dateTo)
                              }
                            </div>
                            <div style={{ 
                              color: 'var(--text-primary)', 
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              marginTop: '0.25rem'
                            }}>
                              KSh {(parseFloat(site.amount) || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                      <hr style={{ borderColor: 'var(--border-color)', margin: '0.75rem 0' }} />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: 700
                      }}>
                        <span style={{ color: 'var(--text-primary)' }}>Total: {guard.sites.length} Sites</span>
                        <span style={{ color: 'rgb(34, 197, 94)', fontSize: '1rem' }}>
                          Total: KSh{guard.sites.reduce((sum, site) => sum + (parseFloat(site.amount) || 0), 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Edit Coverage Modal */}
      <Modal 
        show={showEditModal} 
        onHide={handleCloseEditModal}
        centered
        size="lg"
      >
        <Modal.Header style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: 'none',
          borderBottom: '1px solid var(--border-color)',
          padding: '1.5rem'
        }}>
          <Modal.Title style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <FaEdit style={{ color: 'var(--accent-blue)' }} />
            Edit Coverage
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{
          background: 'var(--bg-primary)',
          padding: '2rem',
          maxHeight: '70vh',
          overflowY: 'auto'
        }}>
          {selectedCoverage && (
            <Form>
              {/* Site Information */}
              <div style={{
                background: 'var(--surface-glass)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h6 style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Site Information
                </h6>
                
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    Site Name <span style={{ color: 'var(--danger)' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.siteName}
                    onChange={(e) => handleEditFormChange('siteName', e.target.value)}
                    placeholder="Enter site name"
                    required
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      padding: '0.75rem'
                    }}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        Branch
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.branch}
                        onChange={(e) => handleEditFormChange('branch', e.target.value)}
                        placeholder="Enter branch"
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--text-primary)',
                          padding: '0.75rem'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        Region
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.region}
                        onChange={(e) => handleEditFormChange('region', e.target.value)}
                        placeholder="Enter region"
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--text-primary)',
                          padding: '0.75rem'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Date Information */}
              <div style={{
                background: 'var(--surface-glass)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h6 style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Date Range
                </h6>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Single Day Coverage"
                    checked={editFormData.isSingleDay}
                    onChange={(e) => handleEditFormChange('isSingleDay', e.target.checked)}
                    style={{ color: 'var(--text-primary)' }}
                  />
                </Form.Group>

                <Row>
                  <Col md={editFormData.isSingleDay ? 12 : 6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {editFormData.isSingleDay ? 'Date' : 'Start Date'} <span style={{ color: 'var(--danger)' }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={editFormData.dateFrom}
                        onChange={(e) => handleEditFormChange('dateFrom', e.target.value)}
                        required
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--text-primary)',
                          padding: '0.75rem'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  {!editFormData.isSingleDay && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          End Date <span style={{ color: 'var(--danger)' }}>*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={editFormData.dateTo}
                          onChange={(e) => handleEditFormChange('dateTo', e.target.value)}
                          min={editFormData.dateFrom}
                          required={!editFormData.isSingleDay}
                          style={{
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text-primary)',
                            padding: '0.75rem'
                          }}
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </div>

              {/* Guards Information */}
              <div style={{
                background: 'var(--surface-glass)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.5rem'
              }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 style={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    marginBottom: 0,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Guards Assignment
                  </h6>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddGuardColumn}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      borderRadius: 'var(--radius-md)',
                      fontWeight: 600
                    }}
                  >
                    <FaPlus />
                    Add Guard
                  </Button>
                </div>

                {Array.from({ length: editFormData.numColumns }).map((_, index) => (
                  <div 
                    key={index} 
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                        Guard {index + 1}
                      </strong>
                      {editFormData.numColumns > 1 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveGuardColumn(index)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '0.75rem'
                          }}
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                            Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={editFormData.guards[index]?.name || ''}
                            onChange={(e) => handleGuardChange(index, 'name', e.target.value)}
                            placeholder="Guard name"
                            style={{
                              background: 'var(--bg-primary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--radius-md)',
                              color: 'var(--text-primary)',
                              padding: '0.6rem'
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                            Phone
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={editFormData.guards[index]?.phone || ''}
                            onChange={(e) => handleGuardChange(index, 'phone', e.target.value)}
                            placeholder="Phone number"
                            style={{
                              background: 'var(--bg-primary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--radius-md)',
                              color: 'var(--text-primary)',
                              padding: '0.6rem'
                            }}
                          />
                        </Form.Group>
                      </Col>
                      
                      {/* Payment Information */}
                      <Col xs={12}>
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                          Payment Status
                        </Form.Label>
                      </Col>
                      <Col md={6}>
                        <div style={{
                          display: 'flex',
                          gap: '0.75rem'
                        }}>
                          <div
                            onClick={() => {
                              handleGuardChange(index, 'payType', 'PAYABLE');
                              const currentAmount = editFormData.guards[index]?.amount;
                              if (!currentAmount || currentAmount === 0) {
                                handleGuardChange(index, 'amount', 500);
                              }
                            }}
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.625rem 0.875rem',
                              background: editFormData.guards[index]?.payType === 'PAYABLE' ? 'var(--accent-blue-dark)' : 'var(--bg-primary)',
                              border: `2px solid ${editFormData.guards[index]?.payType === 'PAYABLE' ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              userSelect: 'none'
                            }}
                          >
                            <div style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              border: `2px solid ${editFormData.guards[index]?.payType === 'PAYABLE' ? 'white' : 'var(--border-color)'}`,
                              background: editFormData.guards[index]?.payType === 'PAYABLE' ? 'var(--accent-blue)' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              transition: 'all 0.2s ease'
                            }}>
                              {editFormData.guards[index]?.payType === 'PAYABLE' && (
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  background: 'white'
                                }} />
                              )}
                            </div>
                            <span style={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: editFormData.guards[index]?.payType === 'PAYABLE' ? 'white' : 'var(--text-primary)'
                            }}>
                              Payable
                            </span>
                          </div>
                          
                          <div
                            onClick={() => {
                              handleGuardChange(index, 'payType', 'NON_PAYABLE');
                              handleGuardChange(index, 'amount', 0);
                            }}
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.625rem 0.875rem',
                              background: editFormData.guards[index]?.payType === 'NON_PAYABLE' ? 'var(--accent-blue-dark)' : 'var(--bg-primary)',
                              border: `2px solid ${editFormData.guards[index]?.payType === 'NON_PAYABLE' ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              userSelect: 'none'
                            }}
                          >
                            <div style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              border: `2px solid ${editFormData.guards[index]?.payType === 'NON_PAYABLE' ? 'white' : 'var(--border-color)'}`,
                              background: editFormData.guards[index]?.payType === 'NON_PAYABLE' ? 'var(--accent-blue)' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              transition: 'all 0.2s ease'
                            }}>
                              {editFormData.guards[index]?.payType === 'NON_PAYABLE' && (
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  background: 'white'
                                }} />
                              )}
                            </div>
                            <span style={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: editFormData.guards[index]?.payType === 'NON_PAYABLE' ? 'white' : 'var(--text-primary)'
                            }}>
                              Non-payable
                            </span>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                            Amount (KSh)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={editFormData.guards[index]?.amount || ''}
                            onChange={(e) => handleGuardChange(index, 'amount', parseFloat(e.target.value) || 0)}
                            placeholder="Enter amount"
                            disabled={editFormData.guards[index]?.payType === 'NON_PAYABLE'}
                            style={{
                              background: editFormData.guards[index]?.payType === 'PAYABLE' ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--radius-md)',
                              color: editFormData.guards[index]?.payType === 'PAYABLE' ? 'var(--text-primary)' : 'var(--text-muted)',
                              padding: '0.6rem',
                              opacity: editFormData.guards[index]?.payType === 'PAYABLE' ? 1 : 0.6,
                              cursor: editFormData.guards[index]?.payType === 'PAYABLE' ? 'text' : 'not-allowed'
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            </Form>
          )}
        </Modal.Body>
        
        <Modal.Footer style={{
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(10px)',
          border: 'none',
          borderTop: '1px solid var(--border-color)',
          padding: '1rem 1.5rem'
        }}>
          <Button
            variant="secondary"
            onClick={handleCloseEditModal}
            disabled={saving}
            style={{
              padding: '0.625rem 1.5rem',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            disabled={saving}
            style={{
              padding: '0.625rem 1.5rem',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" />
                Saving...
              </>
            ) : (
              <>
                <FaEdit />
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CoverageView;
